<template>
    <div>
    <!-- HEADER -->
      <div @click="clearCurrentIssue()" class="d-flex justify-end mt-1 mb-3">
        <v-chip small class="cursor-pointer">
          <v-icon small>mdi-close</v-icon>
        </v-chip>
      </div>
    <v-divider class="mb-2"/>
    <v-card class="pa-0" elevation="1">

      <!-- CARD HEAD -->
      <div class="grey lighten-4 py-2 ps-4 pe-1">
        <div class="d-flex align-start justify-space-between">
          <div class="flex-grow-1 min-width-0">
            <span class="font-weight-medium grey--text text--darken-2 break-word">{{issue.label}}</span>
          </div>
          <PrestaIssueStatusChip class="flex-shrink-0 ml-2" small :value="issue.status"/>
        </div>
        <div class="d-flex align-center flex-wrap py-2 gap2">
          <p class="caption grey--text text--darken-1 font-weight-medium">
            <span class="small-text grey--text text--lighten-1">#</span>{{issue.id}}
          </p>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-chip v-on="on" label small outlined class="grey grey--text lighten-2 text--darken-1 mx-1 caption">
                {{ getTagFromId(issue.tagId) }}
              </v-chip>
            </template>
            Type du litige
          </v-tooltip>
          <p v-if="hasSegments" class="caption grey--text text--darken-1">{{segmentName}}</p>
          <p class="caption pe-3 ms-auto">
            <span class="grey--text caption-xs">Créé le </span>
            <span>{{ _convertDay(issue.createdAt) }}</span>
            <span class="grey--text caption-xs"> à </span>
            <span class="grey--text">{{ _convertHour(issue.createdAt) }}</span>
          </p>
        </div>

      </div>

    <!-- CARD BODY -->
    <div class="pt-1">
      <div class="d-flex flex-column px-2">
        <div class="issue-detail-content" v-if="issue !== null">
          <div v-for="(item, index) in currentComments" :key="`${issue.id}-comment-${index}`" class="d-flex flex-column gap2">
            <!-- COMMENT (normal display) -->
            <div class="pe-4" v-if="!isEditingComment(index)">
            <div class="pl-1"> <!-- [N] Nécessaire pour le cercle de l'effet timeline des comments -->
                <div class="issue-comment-inner-box">

                  <p class="issue-comment">{{ item.comment }}</p>

                  <div class="d-flex justify-space-between align-center gap2">
                    <p class="caption-xs grey--text">
                      le {{ _convertDay(item.createdAt) }} à {{ _convertHour(item.createdAt) }},
                      <span class="font-weight-medium blue-grey--text">{{ item.username }}</span>
                    </p>
                    <v-divider />
                    <div v-if="issue.status === 'in_progress'" class="d-flex gap1">
                      <v-icon x-small @click.stop="onEditComment(index, item)">edit</v-icon>
                      <template v-if="hasMoreThanOneComment">
                        <v-divider vertical />
                        <v-icon x-small @click.stop="onDeleteComment(index)">delete</v-icon>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- EDIT COMMENT -->
            <div v-else class="px-4 py-3">
              <v-form @submit.prevent="onSubmitComment()">
                <v-textarea v-model="commentForm.comment" auto-grow label="Éditer commentaire"/>
                <div class="d-flex align-center justify-end gap2">
                  <smart-btn small secondary @click.native="clearCommentForm()">
                    <span>Annuler</span>
                  </smart-btn>
                  <smart-btn small primary type="submit">
                    <span>Confirmer</span>
                  </smart-btn>
                </div>
              </v-form>
            </div>
          </div>

          <!-- NEW COMMENT -->
          <div v-if="isCreatingComment()" class="d-flex flex-column px-4">
            <v-form class="py-4" @submit.prevent="onSubmitComment()">
              <v-textarea
                  v-model="commentForm.comment"
                  auto-grow
                  label="Nouveau commentaire"
              />

              <div class="d-flex align-center justify-end gap2">
                <smart-btn small secondary @click.native="clearCommentForm()">
                  <span>Annuler</span>
                </smart-btn>

                <smart-btn small primary type="submit">
                  <span>Confirmer</span>
                </smart-btn>
              </div>
            </v-form>
          </div>
        </div>

        <v-divider class="mt-2" />

        <!-- FOOTER -->
        <div class="issue-footer px-4 py-3">
          <template v-if="issueIsInProgress">
            <div class="footer-left">
              <smart-btn small secondary @click.native="onChangeStatus('closed')">
                <v-icon small color="blue lighten-1">mdi-close-box-outline</v-icon>
                <span class="ml-1">Clore le litige</span>
              </smart-btn>

              <smart-btn small secondary @click.native="onChangeStatus('give_up')">
                <v-icon small color="blue lighten-1">mdi-checkbox-blank-outline</v-icon>
                <span class="ml-1">Classer sans suite</span>
              </smart-btn>
            </div>

            <div class="footer-right">
              <v-chip small class="cursor-pointer" @click="onNewComment()">
                <v-icon small color="grey darken-1">mdi-plus</v-icon>
                Nouveau commentaire
              </v-chip>
            </div>
          </template>

          <smart-btn v-else small secondary @click.native="onChangeStatus('in_progress')">
            <v-icon small color="blue lighten-1">mdi-arrow-top-right-bold-box-outline</v-icon>
            <span class="ml-1">Réouvrir le litige</span>
          </smart-btn>
        </div>

      </div>
    </div>

    </v-card>
  </div>
</template>

<script>
import PrestaIssueStatusChip from "@/components/commons/PrestaIssueStatusChip.vue";
import { post } from "@/utils/api";

export default {
  name: "BookingPrestaIssueDetail",
  components: {PrestaIssueStatusChip},
  emits: ['clear', 'updated', 'error', 'status-update'],
  props: {
    issue: { type: Object, required: true },
    tags: { type: Array, required: true },
    hasSegments: { type: [Boolean, null], required: true},
    segmentName: { type: [String, null], default: null }
  },
  data() {
    return {
      commentForm: {
        mode: null, // 'create' | 'edit' | 'delete' | null
        index: null,
        comment: null,
      },
    }
  },
  watch: {
    issue: {
      immediate: true, handler(newVal) {
        if (newVal)
          this.clearCommentForm()
      }
    },
  },
  computed: {
    currentComments() {
      return this.issue?.comments ?? []
    },
    hasMoreThanOneComment() {
      return this.issue?.comments.length > 1 ?? false
    },
    issueIsInProgress() {
      return this.issue.status === 'in_progress'
    },
  },
  methods: {
    getTagFromId(id) {
      if (!this.tags) return "autre"
      return this.tags.find(t => t.id === id)?.tag ?? "autre"
    },
    clearCurrentIssue() {
      this.$emit('clear')
    },
    isEditingComment(index) {
      return this.commentForm.mode === 'edit' && this.commentForm.index === index
    },
    isCreatingComment() {
      return this.commentForm.mode === 'create'
    },
    onSubmitComment() {
      if (this.commentForm.mode !== 'delete' && (!this.commentForm.comment || !this.commentForm.comment.trim().length > 0)) {
        this.clearCommentForm()
        return
      }
      const url = `/api/prestation/${this.issue.prestationId}/prestation-issue/${this.issue.id}`

      const payload = {
        tagId: this.issue.tagId,
        status: this.issue.status,
        label: this.issue.label,
        comments: this.buildComments()
      }

      post(url, payload)
          .json((r) => {
            this.$emit('updated', r)
            this.clearCommentForm()
          })
          .catch ((e) => {
            console.log("[PrestaIssue][onSubmitNewComment] error :", e)
            this.$emit('error', "L'ajout du commentaire a rencontré un problème.")
          })

    },
    buildComment() {
      return {
        userId: this.currentUser.id,
        username: this.username,
        comment: this.commentForm.comment,
      }
    },
    buildComments() {
      if (!this.issue) {
        return []
      }
      const comments = [...this.issue.comments]

      if (this.commentForm.mode === 'delete' && this.commentForm.index !== null) {
        comments.splice(this.commentForm.index, 1)
      } else if (this.commentForm.mode === 'edit' && this.commentForm.index !== null) {
        comments.splice(this.commentForm.index, 1, {
          ...comments[this.commentForm.index],
          comment: this.commentForm.comment,
        })
      } else {
        comments.push(this.buildComment())
      }

      return comments
    },
    onNewComment() {
      this.commentForm.mode = 'create'
      this.commentForm.index = null
      this.commentForm.comment = null
    },
    onEditComment(index, item) {
      this.commentForm.mode = 'edit'
      this.commentForm.index = index
      this.commentForm.comment = item.comment
    },
    onDeleteComment(index) {
      if (!this.hasMoreThanOneComment.value) { return }
      this.commentForm.mode = 'delete'
      this.commentForm.index = index
      this.commentForm.comment = null
      const message = "Êtes-vous sûr de vouloir supprimer ce commentaire ?"
      this._confirmAndRun( message, () => this.onSubmitComment())
    },
    clearCommentForm() {
      this.commentForm.mode = null
      this.commentForm.index = null
      this.commentForm.comment = null
    },
    onChangeStatus(newStatus) {
      this.$emit('status-update', newStatus)
    }
  }, // [N] methods end

}
</script>


<style scoped>

.gap1 {
  gap:4px;
}

.gap2 {
  gap:8px;
}

.cursor-pointer {
  cursor: pointer;
}

.caption-xs {
  font-size: 0.60rem !important;
  font-weight: 400;
  letter-spacing: 0.030em !important;
  line-height: 1.10rem;
}

.issue-detail-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  max-height: 440px;
}

.issue-comment-wrapper {
  padding-left: 4px;
}

.issue-comment-inner-box {
  display: flex;
  flex-direction: column;
  position: relative;
  padding-left: 12px;
  border-left: 1px dotted #E0E0E0;
}

.issue-comment-inner-box::before {
  content: "";
  position: absolute;
  bottom: 5px;
  left: -4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #E0E0E0;
}

.issue-comment {
  white-space: pre-line;
  padding-top: 8px;
}

.issue-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
}

</style>